/**
 * Cabeçalho: identidade, estado da conexão e saída.
 *
 * O estado da conexão fica visível o tempo todo de propósito. Num painel ao
 * vivo, "sem atualização há 5 minutos" e "tudo parado de verdade" são
 * indistinguíveis — a não ser que a tela diga qual dos dois é.
 */
import { motion } from "motion/react";
import { formatTime } from "../lib/format.ts";
import type { ConnectionState } from "../store/fleet.store.ts";
import { Button, Dot } from "./ui";
import { ThemeToggle } from "./ThemeToggle";
import { useMapStore, type View } from "../store/map.store.ts";
import { useAuthStore } from "../store/auth.store.ts";

function ViewSwitch() {
  const view = useMapStore((state) => state.view);
  const setView = useMapStore((state) => state.setView);
  // Vínculos é tela de cadastro e a API devolve 403 para quem não é `admin`.
  // Esconder a aba evita o operador clicar e levar um erro no lugar de uma
  // resposta — a trava de verdade está no servidor, esta é só cortesia.
  //
  // Cadastros aparece também para `operator`: ele cadastra veículo, motorista e
  // base. As abas de dentro que exigem `admin` é que se escondem.
  //
  // Operação aparece para todos, inclusive `viewer`: a fila é LEITURA para ele
  // (a API recusa só o movimento, não a consulta), e esconder a aba tiraria do
  // supervisor e do instrutor justamente a tela que a especificação criou para
  // eles.
  const role = useAuthStore((state) => state.role);
  const options: { id: View; label: string }[] = [
    { id: "map", label: "Mapa" },
    { id: "consumption", label: "Consumo" },
    { id: "alerts", label: "Alertas" },
    { id: "operations", label: "Operação" },
    { id: "registry", label: "Cadastros" },
    ...(role === "admin" ? [{ id: "links" as View, label: "Vínculos" }] : []),
  ];
  return (
    <div className="flex rounded-lg border border-line bg-panel p-0.5" role="tablist">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          role="tab"
          aria-selected={view === option.id}
          onClick={() => setView(option.id)}
          className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
            view === option.id ? "bg-accent text-app" : "text-muted hover:text-ink"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

const CONNECTION: Record<ConnectionState, { label: string; dot: string; pulse: boolean }> = {
  idle: { label: "Desconectado", dot: "bg-muted", pulse: false },
  connecting: { label: "Conectando", dot: "bg-amber-400", pulse: true },
  live: { label: "Ao vivo", dot: "bg-emerald-400", pulse: true },
  reconnecting: { label: "Reconectando", dot: "bg-amber-400", pulse: true },
  offline: { label: "Sem conexão", dot: "bg-rose-400", pulse: false },
};

export function AppHeader({
  connection,
  updatedAt,
  onLogout,
}: {
  connection: ConnectionState;
  updatedAt: string | null;
  onLogout: () => void;
}) {
  const state = CONNECTION[connection];

  return (
    <header className="flex items-center gap-3 border-b border-line bg-app/80 px-4 py-2.5 backdrop-blur">
      {/* Só o símbolo + o nome em texto: o logotipo completo tem o nome
          desenhado dentro e, na altura de um cabeçalho, ele vira borrão.
          Os dois andam juntos num `gap` menor que o do cabeçalho — símbolo e
          nome são uma coisa só, não dois itens da barra. */}
      <div className="flex shrink-0 items-center gap-2">
        <img
          src="/logo-mark.webp"
          alt=""
          aria-hidden
          width={256}
          height={256}
          decoding="async"
          className="size-6 rounded-md ring-1 ring-black/10 dark:ring-white/10"
        />
        <span className="text-sm font-semibold tracking-tight text-ink">MOB Telemetria</span>
      </div>

      <div className="ml-2 flex items-center gap-1.5 rounded-full bg-panel px-2.5 py-1">
        <motion.span
          animate={state.pulse ? { opacity: [1, 0.35, 1] } : { opacity: 1 }}
          transition={{ duration: 1.6, repeat: Infinity }}
        >
          <Dot className={state.dot} />
        </motion.span>
        <span className="text-[11px] text-muted">{state.label}</span>
      </div>

      <ViewSwitch />

      {updatedAt ? (
        <span className="hidden text-[11px] text-faint sm:inline">atualizado {formatTime(updatedAt)}</span>
      ) : null}

      <div className="ml-auto flex items-center gap-2">
        <ThemeToggle />
        <Button variant="ghost" onClick={onLogout} className="px-3 py-1 text-xs">
          Sair
        </Button>
      </div>
    </header>
  );
}
